// @flow

import React from 'react';
import type { Node } from 'react';
import { useIntl } from 'react-intl';
import { useParams, useHistory } from 'react-router-dom';
import { article as ENUM_ARTICLE, HTTP_STATUS, meta as META_ENUM } from 'Enum';
import { useQuery } from 'shared_services/apollo/useQuery';
import { useUrlParams } from 'shared_data/providers/url/useUrlParams';
import { LocalizedConfig } from 'shared_services/riseart/utils/LocalizedConfig';
import { generateSrcForCDN } from 'shared_components/common/artdirection/picture/Picture';
import { gql404RedirectHandler } from 'shared_services/riseart/utils/RouteUtils';
import { IsomorphicRipple } from 'shared_components/common/preloader/IsomorphicRipple';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { extractImageFileFromData } from 'shared_services/riseart/utils/ImageUtils';
import READ_ARTICLE from 'shared_data/queries/articles/read.graphql';
import { MetaProvider } from 'shared_data/providers/meta/Meta';
import { generateHrefLangParams } from 'shared_services/riseart/meta/utils';
import { LDTYPES, generateLDJSON } from 'shared_models/seo/ldJSON';
import { LocationManager } from 'shared_services/riseart/url/Location';

type Props = {
  children: Function,
  staticContext: ?Object,
  articleType: string,
  translationPrefix: string,
  metaSubscriber: string,
};

/**
 * BlogArticleQuery
 *
 * @param {Props} props
 * @returns {Node}
 */
export function BlogArticleQuery({
  staticContext,
  translationPrefix,
  articleType,
  metaSubscriber,
  children,
}: Props): Node {
  const intl = useIntl();
  const history = useHistory();
  const { id: selectedAticleId } = useParams();
  const { translatedLocation: location } = useUrlParams();
  const { loading, data, error } = useQuery(READ_ARTICLE, {
    variables: { id: selectedAticleId },
    context: {
      customOptions: {
        errorSuppressFromResponse: true,
        errorHandler: gql404RedirectHandler(
          // $FlowFixMe
          UrlAssembler.articleList(articleType, {
            search: {
              [LocalizedConfig.get('navigation.uri.query.params.message')]: HTTP_STATUS.NOT_FOUND,
            },
          }),
          history,
          staticContext,
        ),
      },
    },
  });

  if (error || !selectedAticleId) {
    return null;
  }

  const { readArticle: article } = data || {};
  const articleCategory = (article && article.categories && article.categories[0]) || {};
  const articleCategoryUrl = articleCategory
    ? UrlAssembler.articleCategory(articleCategory.uri, articleCategory.type)
    : null;
  const authorUrl =
    article && article.author
      ? UrlAssembler.authorDetail(
          article.author.type,
          article.author.typeId,
          article.author.typeAlias,
        )
      : null;
  const bannerImage =
    article &&
    article.images &&
    extractImageFileFromData.articleImage(
      article.images,
      ENUM_ARTICLE.image.type.TYPE_BANNER_MASTER,
    );
  const canonicalURL = article
    ? UrlAssembler.articleDetail(article.id, article.slug, article.type, {
        origin: LocationManager.get('origin'),
      })
    : LocationManager.get('originWithPath');
  const hrefLangParams = article ? generateHrefLangParams(article.locales) : null;
  const metaData =
    data && article
      ? {
          title:
            article.metaTitle ||
            intl.formatMessage(
              { id: `meta.${translationPrefix}.dynamicMetaTitle` },
              { title: article.title },
            ),
          description:
            article.metaDescription ||
            intl.formatMessage(
              { id: `meta.${translationPrefix}.dynamicMetaDescription` },
              { summary: article.summary, title: article.title },
            ),
          keywords:
            article.metaKeywords ||
            intl.formatMessage(
              { id: `meta.${translationPrefix}.dynamicMetaKeywords` },
              {
                title: article.title,
                tags: article.tags && article.tags.join(' ,'),
              },
            ),
          image: bannerImage
            ? generateSrcForCDN({
                ...bannerImage,
                width: 1200,
              })
            : null,
        }
      : {};

  return (
    <IsomorphicRipple isActive={loading} isFullScreen={!article}>
      {!loading ? (
        <MetaProvider
          metaSubscriber={metaSubscriber}
          meta={
            data && article
              ? {
                  [META_ENUM.METATYPE.LINK_CANONICAL]: canonicalURL,
                  [META_ENUM.METATYPE.LINK_CANONICAL_REDIRECT]: UrlAssembler.articleDetail(
                    article.id,
                    article.slug,
                    article.type,
                    {
                      ...(location.search ? { search: location.search } : null),
                      ...(location.hash ? { hash: location.hash } : null),
                    },
                  ),
                  [META_ENUM.METATYPE.LINK_HREFLANG]: hrefLangParams,
                  [META_ENUM.METATYPE.LINK_HREFLANG_REDIRECT]: hrefLangParams,
                  [META_ENUM.METATYPE.META_TITLE]: metaData.title,
                  [META_ENUM.METATYPE.META_KEYWORDS]: metaData.keywords,
                  [META_ENUM.METATYPE.META_DESCRIPTION]: metaData.description,
                  [META_ENUM.METATYPE.META_OG_TITLE]: metaData.title,
                  [META_ENUM.METATYPE.META_OG_DESCRIPTION]: metaData.description,
                  [META_ENUM.METATYPE.META_OG_URL]: canonicalURL,
                  [META_ENUM.METATYPE.META_OG_IMAGE]: metaData.image,
                  [META_ENUM.METATYPE.SCRIPT_LD_JSON_ARTICLE]: generateLDJSON(LDTYPES.ARTICLE, {
                    title: article.title,
                    summary: article.summary,
                    author: {
                      fullName: article.author.fullName,
                      url: authorUrl ? `${LocationManager.get('origin')}${authorUrl}` : null,
                    },
                    publishedDate: article.publishedDate,
                    image: metaData.image,
                    url: canonicalURL,
                    mainEntityOfPage: {
                      type: 'WebPage',
                      url: canonicalURL,
                    },
                  }),
                }
              : null
          }
          hasError={!!error || !data}
        />
      ) : null}
      {children({
        loading,
        error,
        article,
        articleCategory,
        articleCategoryUrl,
        authorUrl,
        bannerImage,
        metaData,
        canonicalURL,
      })}
    </IsomorphicRipple>
  );
}
